import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DashboardLayout } from './Dashboard';
import {
  DataQualityBadge, InsufficientCard, DriversSection, PriorityActions,
  ComparisonCard, IndicatorDetails, LimitationsCard, HonestyDisclaimer,
} from '../components/WinnerScoreIntegrity';
import {
  Trophy, Lightning, ArrowRight, Link as LinkIcon, Target, Sparkle,
  WarningCircle, ChartLineUp, TrendUp, Rocket, Wrench, Clock,
  Star, CheckCircle, XCircle, ArrowUpRight, Crown, ShieldWarning,
  Globe, GlobeSimple,
} from '@phosphor-icons/react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CLASSIFICATION_MAP = {
  excelente: { color: '#10B981', bg: 'from-[#10B981]/20', border: '#10B981' },
  boa: { color: '#3B82F6', bg: 'from-[#3B82F6]/20', border: '#3B82F6' },
  moderada: { color: '#F59E0B', bg: 'from-[#F59E0B]/20', border: '#F59E0B' },
  evitar: { color: '#EF4444', bg: 'from-[#EF4444]/20', border: '#EF4444' },
};

const LEVEL_COLORS = {
  baixo: '#10B981',
  medio: '#F59E0B',
  alto: '#3B82F6',
  muito_alto: '#A855F7',
  facil: '#10B981',
  media: '#F59E0B',
  dificil: '#EF4444',
  muito_dificil: '#7F1D1D',
};

const colorOf = (key, fallback = '#9CA3AF') => LEVEL_COLORS[key] || fallback;
const scoreColor = (s) => (s >= 80 ? '#10B981' : s >= 60 ? '#3B82F6' : s >= 40 ? '#F59E0B' : '#EF4444');

const ScoreRing = ({ score }) => {
  const color = scoreColor(score);
  const circumference = 2 * Math.PI * 60;
  const offset = circumference - (Math.max(0, Math.min(100, score)) / 100) * circumference;
  return (
    <div className="relative w-40 h-40 flex items-center justify-center" data-testid="winner-score-ring">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r="60" stroke="rgba(255,255,255,0.06)" strokeWidth="10" fill="none" />
        <circle
          cx="70" cy="70" r="60"
          stroke={color} strokeWidth="10" fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
        />
      </svg>
      <div className="text-center">
        <div className="text-5xl font-[Outfit] font-bold leading-none" style={{ color }}>{score}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">Winner Score™</div>
      </div>
    </div>
  );
};

const PillStat = ({ icon: Icon, label, valueKey, color }) => {
  const { t } = useTranslation();
  const c = colorOf(valueKey, color);
  return (
    <div className="p-4 bg-[#0A0A0A] border border-white/10 rounded-xl">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} weight="bold" style={{ color: c }} />
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</span>
      </div>
      <div className="text-base font-[Outfit] font-bold" style={{ color: c }}>{valueKey ? t(`ws.lvl_${valueKey}`, { defaultValue: valueKey }) : '—'}</div>
    </div>
  );
};

const ListSection = ({ title, items, icon: Icon, iconColor, testid }) => (
  <div className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} weight="bold" style={{ color: iconColor }} />
      <h3 className="font-[Outfit] font-bold text-base">{title}</h3>
    </div>
    <ul className="space-y-2.5">
      {(items || []).map((it, i) => (
        <li key={i} className="text-sm text-white/80 leading-relaxed flex items-start gap-2">
          <span className="text-white/30 font-[Outfit] font-bold text-xs mt-0.5 flex-shrink-0">{(i + 1).toString().padStart(2, '0')}</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const WinnerScorePage = ({ user }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState('');
  const [context, setContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [quota, setQuota] = useState(null);
  const [publishToHall, setPublishToHall] = useState(true);
  const [publishing, setPublishing] = useState(false);

  const fetchQuota = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/winner-score/quota`, { withCredentials: true });
      setQuota(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/winner-score/history`, { withCredentials: true });
      setHistory(res.data.items || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchQuota();
    fetchHistory();
    const prefilled = searchParams.get('url');
    if (prefilled) setUrl(prefilled);
  }, [fetchQuota, fetchHistory, searchParams]);

  const recoverResult = async (targetUrl) => {
    for (let i = 0; i < 18; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const h = await axios.get(`${BACKEND_URL}/api/winner-score/history`, { withCredentials: true });
        const item = (h.data.items || []).find(
          (x) => x.url === targetUrl && Date.now() - new Date(x.created_at).getTime() < 15 * 60 * 1000
        );
        if (item) {
          const det = await axios.get(`${BACKEND_URL}/api/winner-score/${item.score_id}`, { withCredentials: true });
          setResult(det.data);
          if (det.data.status === 'insufficient') {
            toast.warning(t('ws.insufficient_toast'));
          } else {
            toast.success(`Winner Score™: ${det.data.winner_score}/100`);
          }
          fetchQuota();
          fetchHistory();
          return true;
        }
      } catch (e) { /* continua a tentar */ }
    }
    return false;
  };

  const analyze = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/winner-score/analyze`,
        { url: url.trim(), context: context.trim() || null, publish_to_hall: publishToHall },
        { withCredentials: true, timeout: 90000 }
      );
      setResult(res.data);
      if (res.data.status === 'insufficient') {
        toast.warning(t('ws.insufficient_toast'));
      } else {
        toast.success(`Winner Score™: ${res.data.winner_score}/100`);
        if (res.data.hall_reward > 0) {
          toast.success(t('ws.toast_hall_reward', { n: res.data.hall_reward }));
        }
      }
      fetchQuota();
      fetchHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (!detail) {
        // Ligação cortada pelo proxy em análises longas — o servidor continua a processar.
        toast.info(t('ws.recovering'));
        const recovered = await recoverResult(url.trim());
        if (!recovered) toast.error(t('ws.err_analyze'));
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async () => {
    if (!result?.score_id) return;
    setPublishing(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/winner-score/${result.score_id}/publish`,
        { published: !result.published },
        { withCredentials: true }
      );
      setResult((prev) => ({ ...prev, published: res.data.published }));
      toast.success(res.data.published
        ? t('ws.published_toast')
        : t('ws.unpublished_toast'));
      if (res.data.hall_reward > 0) {
        toast.success(t('ws.toast_hall_reward', { n: res.data.hall_reward }));
      }
      fetchHistory();
    } catch (e) {
      toast.error(t('ws.err_publish'));
    } finally {
      setPublishing(false);
    }
  };

  const loadHistoryItem = async (sid) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/winner-score/${sid}`, { withCredentials: true });
      setResult(res.data);
      setUrl(res.data.url);
    } catch (e) {
      toast.error(t('ws.err_open'));
    } finally {
      setLoading(false);
    }
  };

  const radarData = useMemo(() => {
    const ind = result?.data?.indicators;
    if (!ind) return [];
    const groupAvg = (group) => {
      const values = Object.values(group || {}).filter((v) => typeof v === 'number');
      if (!values.length) return 0;
      return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10);
    };
    return [
      { axis: t('ws.axis_oferta'), value: groupAvg(ind.oferta) },
      { axis: t('ws.axis_mercado'), value: groupAvg(ind.mercado) },
      { axis: t('ws.axis_anuncios'), value: groupAvg(ind.anuncios) },
      { axis: t('ws.axis_funil'), value: groupAvg(ind.funil) },
      { axis: t('ws.axis_monetizacao'), value: groupAvg(ind.monetizacao) },
      { axis: t('ws.axis_ia'), value: groupAvg(ind.ia_predictive) },
    ];
  }, [result, t]);

  const classification = result?.data?.classification && CLASSIFICATION_MAP[result.data.classification];

  return (
    <DashboardLayout user={user} activePage="/winner-score">
      <div className="p-6" data-testid="winner-score-page">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#F59E0B]/15 to-[#FBBF24]/10 border border-[#F59E0B]/30 rounded-full mb-3">
              <Trophy size={14} weight="fill" className="text-[#F59E0B]" />
              <span className="text-[11px] tracking-[0.25em] uppercase text-[#F59E0B] font-semibold">{t('ws.kicker')}</span>
            </div>
            <h1 className="text-4xl font-[Outfit] font-bold tracking-tight mb-2">
              Winner Score™
            </h1>
            <p className="text-white/60 max-w-2xl">
              {t('ws.sub_pre')} <strong className="text-white/90">{t('ws.sub_strong')}</strong> {t('ws.sub_post')}
            </p>
          </div>
          {quota && (
            <div className="px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-right min-w-[180px]" data-testid="winner-score-quota">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{t('ws.quota_label')}</div>
              <div className="font-[Outfit] font-bold text-2xl">
                {quota.used}<span className="text-white/30 text-base"> / {quota.limit}</span>
              </div>
              <div className="text-[10px] text-white/40 mt-0.5 capitalize">{t('ws.plan')} {quota.plan.replace('_', ' ')}</div>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={analyze} className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl mb-6" data-testid="winner-score-form">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-6">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('ws.url_label')}</label>
              <div className="relative">
                <LinkIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="url"
                  required
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://exemplo.com/oferta"
                  data-testid="winner-score-url-input"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#F59E0B]/50 transition"
                />
              </div>
            </div>
            <div className="lg:col-span-4">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('ws.ctx_label')}</label>
              <input
                type="text"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder={t('ws.ctx_ph')}
                data-testid="winner-score-context-input"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#F59E0B]/50 transition"
              />
            </div>
            <div className="lg:col-span-2 flex items-end">
              <button
                type="submit"
                disabled={loading || !url.trim()}
                data-testid="winner-score-analyze-btn"
                className="w-full px-5 py-3 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-semibold text-black transition flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
              >
                {loading ? t('ws.analyzing') : (
                  <>
                    <Trophy size={16} weight="fill" />
                    {t('ws.calc')}
                  </>
                )}
              </button>
            </div>
          </div>
          <label
            className="mt-4 flex items-start gap-3 cursor-pointer select-none group"
            data-testid="winner-score-publish-optin"
          >
            <input
              type="checkbox"
              checked={publishToHall}
              onChange={(e) => setPublishToHall(e.target.checked)}
              data-testid="winner-score-publish-checkbox"
              className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 accent-[#F59E0B] cursor-pointer"
            />
            <span className="text-sm text-white/60 group-hover:text-white/80 transition leading-relaxed">
              <Globe size={14} weight="bold" className="inline mr-1.5 text-[#F59E0B]" />
              {t('ws.optin_pre')} <strong className="text-white/90">Hall of Winners</strong>
              <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#34D399] border border-[#10B981]/30 text-[10px] font-bold align-middle" data-testid="hall-reward-badge">{t('ws.optin_badge')}</span>
              <span className="text-white/40">{t('ws.optin_post')}</span>
            </span>
          </label>
        </form>

        {/* Loading */}
        {loading && !result && (
          <div className="p-12 bg-[#0A0A0A] border border-white/10 rounded-2xl text-center" data-testid="winner-score-loading">
            <Sparkle size={36} weight="fill" className="mx-auto mb-4 text-[#F59E0B] animate-pulse" />
            <p className="text-white/80 mb-1 text-lg font-[Outfit]">{t('ws.loading_1')}</p>
            <p className="text-white/40 text-sm">{t('ws.loading_2')}</p>
          </div>
        )}

        {/* Falha segura — dados insuficientes, sem score definitivo */}
        {result && result.status === 'insufficient' && (
          <InsufficientCard
            result={result}
            onRetry={() => { setResult(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          />
        )}

        {/* Result */}
        {result && result.status !== 'insufficient' && (
          <div className="space-y-6">
            {/* Top card */}
            <div
              className={`relative p-6 md:p-8 rounded-3xl border bg-gradient-to-br ${classification?.bg || 'from-white/[0.04]'} to-transparent`}
              style={{ borderColor: `${classification?.border || 'rgba(255,255,255,0.1)'}44` }}
              data-testid="winner-score-result-summary"
            >
              {result.from_cache && (
                <div className="absolute top-4 right-4 px-2 py-1 bg-white/5 border border-white/10 rounded text-[10px] uppercase tracking-wider text-white/40">
                  Cache 24h
                </div>
              )}
              {result.published && (
                <div className="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 bg-[#10B981]/15 border border-[#10B981]/40 rounded-full text-[10px] uppercase tracking-wider text-[#10B981] font-semibold" data-testid="winner-score-published-badge">
                  <GlobeSimple size={11} weight="bold" />
                  Hall of Winners
                </div>
              )}
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
                <ScoreRing score={result.winner_score || 0} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span
                      className="px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                      style={{ background: `${classification?.color || '#9CA3AF'}22`, color: classification?.color || '#9CA3AF' }}
                    >
                      {result.data?.classification ? t(`ws.class_${result.data.classification}`, { defaultValue: result.data.classification }) : t('ws.no_class')}
                    </span>
                    {result.data?.estimated_ticket_eur && (
                      <span className="px-2 py-1 bg-white/5 border border-white/10 rounded text-[11px] text-white/60">
                        {t('ws.ticket', { n: result.data.estimated_ticket_eur })}
                      </span>
                    )}
                    <DataQualityBadge dq={result.data_quality} confidence={result.data?.confidence_overall} />
                  </div>
                  <h2 className="text-2xl font-[Outfit] font-bold mb-3 break-words">
                    <a href={result.url} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition inline-flex items-center gap-2">
                      {result.url.replace(/^https?:\/\//, '').slice(0, 60)}
                      <ArrowUpRight size={14} weight="bold" className="text-white/40" />
                    </a>
                  </h2>
                  <p className="text-white/70 leading-relaxed">{result.data?.summary}</p>
                </div>
              </div>
            </div>

            {/* 4 stat pills */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <PillStat icon={ShieldWarning} label={t('ws.pill_risk')} valueKey={result.data?.risk_level} color="#F59E0B" />
              <PillStat icon={ChartLineUp} label={t('ws.pill_profit')} valueKey={result.data?.profit_potential} color="#10B981" />
              <PillStat icon={Rocket} label={t('ws.pill_scal')} valueKey={result.data?.scalability_potential} color="#3B82F6" />
              <PillStat icon={Wrench} label={t('ws.pill_diff')} valueKey={result.data?.implementation_difficulty} color="#A855F7" />
            </div>

            {result.comparison && <ComparisonCard comparison={result.comparison} />}

            {/* Radar + Time to launch */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl" data-testid="winner-score-radar">
                <h3 className="font-[Outfit] font-bold mb-2 flex items-center gap-2">
                  <Target size={16} weight="bold" className="text-[#3B82F6]" />
                  {t('ws.radar_title')}
                </h3>
                <p className="text-xs text-white/40 mb-4">{t('ws.radar_sub')}</p>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="rgba(255,255,255,0.08)" />
                    <PolarAngleAxis dataKey="axis" tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} stroke="rgba(255,255,255,0.1)" />
                    <Radar name="Score" dataKey="value" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.25} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl space-y-4" data-testid="winner-score-meta">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock size={14} weight="bold" className="text-white/50" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t('ws.launch_time')}</span>
                  </div>
                  <div className="font-[Outfit] font-bold text-lg">{result.data?.estimated_launch_time || '—'}</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Crown size={14} weight="bold" className="text-white/50" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t('ws.ltv')}</span>
                  </div>
                  <div className="font-[Outfit] font-bold text-lg">
                    {result.data?.estimated_ltv_eur ? `€${result.data.estimated_ltv_eur}` : '—'}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Star size={14} weight="bold" className="text-white/50" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t('ws.model')}</span>
                  </div>
                  <div className="font-[Outfit] font-bold text-lg">{result.data?.modelable_score || '—'}<span className="text-white/30 text-sm">/100</span></div>
                </div>
              </div>
            </div>

            {(result.data?.score_drivers_up?.length > 0 || result.data?.score_drivers_down?.length > 0) && (
              <DriversSection up={result.data?.score_drivers_up} down={result.data?.score_drivers_down} />
            )}

            {/* Strengths / Weaknesses / Opportunities */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ListSection
                title={t('ws.strengths')}
                items={result.data?.top_strengths}
                icon={CheckCircle}
                iconColor="#10B981"
                testid="winner-score-strengths"
              />
              <ListSection
                title={t('ws.weaknesses')}
                items={result.data?.top_weaknesses}
                icon={XCircle}
                iconColor="#EF4444"
                testid="winner-score-weaknesses"
              />
              <ListSection
                title={t('ws.improvements')}
                items={result.data?.improvement_opportunities}
                icon={Lightning}
                iconColor="#3B82F6"
                testid="winner-score-opportunities"
              />
            </div>

            {result.data?.priority_actions?.length > 0 && <PriorityActions items={result.data.priority_actions} />}

            {result.data?.indicator_details && <IndicatorDetails details={result.data.indicator_details} />}

            {/* AI recommendation + warnings */}
            {result.data?.ai_recommendation && (
              <div className="p-6 bg-gradient-to-br from-[#3B82F6]/10 to-transparent border border-[#3B82F6]/30 rounded-2xl" data-testid="winner-score-recommendation">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkle size={18} weight="fill" className="text-[#3B82F6]" />
                  <h3 className="font-[Outfit] font-bold">{t('ws.ai_rec')}</h3>
                </div>
                <p className="text-white/80 leading-relaxed">{result.data.ai_recommendation}</p>
              </div>
            )}

            {result.data?.warning_flags && result.data.warning_flags.length > 0 && (
              <div className="p-5 bg-[#7F1D1D]/20 border border-red-500/40 rounded-2xl" data-testid="winner-score-warnings">
                <div className="flex items-center gap-2 mb-3">
                  <WarningCircle size={18} weight="bold" className="text-red-400" />
                  <h3 className="font-[Outfit] font-bold text-red-200">{t('ws.warnings')}</h3>
                </div>
                <ul className="space-y-1.5 text-sm text-red-100/90">
                  {result.data.warning_flags.map((w, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-red-400 mt-0.5">⚠</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <LimitationsCard items={result.data?.limitations} />
            <HonestyDisclaimer />

            {/* Actions */}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={togglePublish}
                disabled={publishing}
                data-testid="winner-score-action-publish"
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition border disabled:opacity-50 ${
                  result.published
                    ? 'bg-[#10B981]/15 hover:bg-[#10B981]/25 border-[#10B981]/40 text-[#10B981]'
                    : 'bg-[#F59E0B]/15 hover:bg-[#F59E0B]/25 border-[#F59E0B]/40 text-[#F59E0B]'
                }`}
              >
                {result.published ? (
                  <>
                    <CheckCircle size={14} weight="fill" />
                    {t('ws.in_hall_remove')}
                  </>
                ) : (
                  <>
                    <Trophy size={14} weight="bold" />
                    {t('ws.publish_hall')}
                  </>
                )}
              </button>
              <button
                onClick={() => navigate(`/funnel-spy?url=${encodeURIComponent(result.url)}`)}
                data-testid="winner-score-action-spy"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#3B82F6]/15 hover:bg-[#3B82F6]/25 border border-[#3B82F6]/40 rounded-xl text-sm font-medium text-[#3B82F6] transition"
              >
                <Target size={14} weight="bold" />
                {t('ws.full_spy')}
              </button>
              <button
                onClick={() => navigate('/ai-composer', { state: { sourceUrl: result.url } })}
                data-testid="winner-score-action-composer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm font-medium transition"
              >
                <Sparkle size={14} weight="bold" />
                {t('ws.gen_copy')}
              </button>
              <button
                onClick={() => { setResult(null); setUrl(''); setContext(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                data-testid="winner-score-action-new"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm font-medium transition"
              >
                <ArrowRight size={14} weight="bold" />
                {t('ws.new_analysis')}
              </button>
            </div>
          </div>
        )}

        {/* History */}
        {!result && history.length > 0 && (
          <div className="mt-8" data-testid="winner-score-history">
            <h3 className="text-sm uppercase tracking-[0.2em] text-white/40 mb-3">{t('ws.history')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {history.map((h) => (
                <button
                  key={h.score_id}
                  onClick={() => loadHistoryItem(h.score_id)}
                  data-testid={`history-${h.score_id}`}
                  className="text-left p-4 bg-[#0A0A0A] border border-white/10 rounded-xl hover:border-white/20 transition group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center font-[Outfit] font-bold text-sm border"
                      style={{
                        background: `${scoreColor(h.winner_score)}11`,
                        color: scoreColor(h.winner_score),
                        borderColor: `${scoreColor(h.winner_score)}44`
                      }}
                    >
                      {h.status === 'insufficient' ? '—' : (h.winner_score || 0)}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-white/40">{new Date(h.created_at).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : 'en-US')}</span>
                  </div>
                  <div className="text-sm font-[Outfit] font-bold text-white truncate" title={h.url}>
                    {h.url.replace(/^https?:\/\//, '').slice(0, 40)}
                  </div>
                  <div className="text-xs text-white/50 mt-1 line-clamp-2">{h.status === 'insufficient' ? t('ws.history_insufficient') : (h.data?.summary || '—')}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WinnerScorePage;
